<script lang="ts">
  import { onMount } from 'svelte';
  import { listLawyers } from '@shared/api/lawyers';
  import { reqArray, reqRecord } from '@shared/value-objects/parse-helpers';

  /// 表示に必要な最小形。shared は domain (branded Lawyer) を import できないため、
  /// API の unknown レスポンスからこの形だけを取り出す (value は plain string id)。
  interface LawyerOption {
    id: string;
    full_name: string;
    is_representative: boolean;
    position: string | null;
  }

  interface Props {
    /// Currently selected lawyer id, or `''` for "未選択".
    value: string;
    /// When set, only lawyers belonging to this firm are listed. When empty,
    /// the first page of all lawyers (up to page_size=200) is fetched —
    /// useful for "global" pickers like SalesAction targets.
    firmId?: string;
    optional?: boolean;
    placeholder?: string;
    disabled?: boolean;
    id?: string;
  }

  let {
    value = $bindable(''),
    firmId,
    optional = true,
    placeholder = '弁護士を選択',
    disabled = false,
    id,
  }: Props = $props();

  let lawyers = $state<LawyerOption[]>([]);
  let loaded = $state(false);
  let error = $state<string | null>(null);

  // Re-fetch when `firmId` changes so list/firmless and firm-scoped pickers
  // stay in sync.
  let lastFetchedFirmId: string | undefined = undefined;
  $effect(() => {
    if (firmId === lastFetchedFirmId) return;
    lastFetchedFirmId = firmId;
    void load();
  });

  onMount(() => {
    void load();
  });

  async function load() {
    loaded = false;
    error = null;
    try {
      const res = await listLawyers({
        firm_id: firmId,
        page_size: 200,
        sort_by: 'full_name',
        sort_dir: 'asc',
      });
      const items = reqArray(reqRecord(res, 'lawyers').items, 'lawyers.items');
      lawyers = items.map((it) => {
        const o = reqRecord(it, 'lawyer');
        return {
          id: String(o.id ?? ''),
          full_name: String(o.full_name ?? ''),
          is_representative: o.is_representative === true,
          position: o.position == null ? null : String(o.position),
        };
      });
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
      lawyers = [];
    } finally {
      loaded = true;
    }
  }

  function label(l: LawyerOption): string {
    const name = l.full_name;
    const role = l.is_representative ? ' (代表)' : l.position ? ` (${l.position})` : '';
    return `${name}${role}`;
  }
</script>

<select bind:value {id} {disabled}>
  {#if optional}
    <option value="">{placeholder}</option>
  {/if}
  {#each lawyers as l (l.id)}
    <option value={l.id}>{label(l)}</option>
  {/each}
</select>
{#if !loaded}
  <small class="hint">読込中…</small>
{/if}
{#if error}
  <small class="error">弁護士の取得に失敗しました: {error}</small>
{/if}

<style>
  select {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
  }
  .hint { color: #6b7280; font-size: 0.7rem; }
  .error { color: #b91c1c; font-size: 0.7rem; }
</style>
